import { useState, useEffect } from "react";
import SimpleBeforeAfter from "@/components/SimpleBeforeAfter";
import { Carousel, CarouselContent, CarouselItem, CarouselApi } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { trackGalleryInteraction } from "@/lib/analytics";

interface Transformation {
  before: string;
  after: string;
  petType: string;
  description: string;
  beforeAlt: string;
  afterAlt: string;
}

interface TransformationShowcaseProps {
  transformations: Transformation[];
}

const TransformationShowcase = ({ 
  transformations 
}: TransformationShowcaseProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Limit to first 4 transformations for hero carousel
  const carouselTransformations = transformations.slice(0, 4);
  const currentTransformation = carouselTransformations[currentIndex];
  
  // Auto-play plugin configuration
  const autoplayPlugin = Autoplay({
    delay: 5000,
    stopOnInteraction: true,
    stopOnMouseEnter: true,
  });
  
  // Update current index when carousel changes
  useEffect(() => {
    if (!api) return;
    
    api.on("select", () => {
      setCurrentIndex(api.selectedScrollSnap());
    });
  }, [api]);
  
  return (
    <div className="relative">
      {/* Diagonal Background Element */}
      <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
        <svg
          className="absolute -top-20 -right-20 w-[600px] h-[600px] opacity-20"
          viewBox="0 0 600 600"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 300 L300 0 L600 300 L300 600 Z"
            fill="url(#diagonal-gradient)"
          />
          <defs>
            <linearGradient id="diagonal-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
              <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity="0.1" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      
      {/* Auto-Play Carousel Hero Section */}
      <Carousel
        setApi={setApi}
        plugins={[autoplayPlugin as any]}
        className="w-full"
        opts={{
          loop: true,
          duration: 30,
        }}
      >
        <CarouselContent>
          {carouselTransformations.map((transformation, index) => (
            <CarouselItem key={index}>
              <div className="relative h-[500px] md:h-[600px] lg:h-[700px] rounded-2xl overflow-hidden shadow-2xl">
                <SimpleBeforeAfter
                  before={transformation.before}
                  after={transformation.after}
                  beforeAlt={transformation.beforeAlt}
                  afterAlt={transformation.afterAlt}
                />
                
                {/* Overlay Content */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background/95 via-background/80 to-transparent p-6 md:p-10 pointer-events-none">
                  <div className="max-w-4xl mx-auto">
                    <div className="inline-block mb-3">
                      <span className="text-sm font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full">
                        ✨ Transformation
                      </span>
                    </div>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3">
                      {transformation.petType}
                    </h2>
                    <p className="text-base md:text-lg text-muted-foreground mb-6">
                      {transformation.description}
                    </p>
                    
                    {/* Counter */}
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-muted-foreground">
                        {currentIndex + 1} / {carouselTransformations.length}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        
        {/* Dots Indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {carouselTransformations.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                api?.scrollTo(index);
                trackGalleryInteraction('carousel_click', carouselTransformations[index].petType, 'homepage_hero_dot');
              }}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'w-8 bg-primary' 
                  : 'w-2 bg-muted-foreground/40 hover:bg-muted-foreground/60'
              }`}
              aria-label={`Go to transformation ${index + 1}`}
            />
          ))}
        </div>
      </Carousel>
    </div>
  );
};

export default TransformationShowcase;
